import React, { useState, useMemo, ReactNode } from "react";
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Switch,
  FormControlLabel,
  Badge,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  List as ListIcon,
  Add as AddIcon,
  Dashboard as DashboardIcon,
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Store as StoreIcon,
  Science as ScienceIcon,
  AutoFixHigh as AutoFixHighIcon,
  QuestionAnswer as QuestionAnswerIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import { useTheme as useCustomTheme } from "../../contexts/ThemeContext";
import { useUnreadNotificationCount } from "../../hooks/useApi";
import { auth } from "../../firebase";
import { signOut } from "firebase/auth";

const drawerWidth = 280;

interface AppLayoutProps {
  children: ReactNode;
}

interface NavigationItem {
  text: string;
  icon: ReactNode;
  path: string;
  roles?: string[];
  public?: boolean;
}

const navigationItems: NavigationItem[] = [
  { text: "ホーム", icon: <HomeIcon />, path: "/", public: true },
  { text: "落とし物一覧", icon: <ListIcon />, path: "/items", public: true },
  {
    text: "新規登録",
    icon: <AddIcon />,
    path: "/register",
    roles: ["staff", "admin"],
  },
  {
    text: "AI自動登録",
    icon: <AutoFixHighIcon />,
    path: "/auto-register",
    roles: ["staff", "admin"],
  },
  { text: "検索", icon: <SearchIcon />, path: "/search", public: true },
  { text: "問い合わせ", icon: <QuestionAnswerIcon />, path: "/inquiries" },
  { text: "通知", icon: <NotificationsIcon />, path: "/notifications" },
  {
    text: "ダッシュボード",
    icon: <DashboardIcon />,
    path: "/dashboard",
    roles: ["staff", "admin"],
  },
  {
    text: "ユーザー管理",
    icon: <SupervisorAccountIcon />,
    path: "/admin/users",
    roles: ["admin"],
  },
  {
    text: "店舗管理",
    icon: <StoreIcon />,
    path: "/admin/stores",
    roles: ["admin"],
  },
  {
    text: "店舗選択デモ",
    icon: <ScienceIcon />,
    path: "/demo/store-select",
    roles: ["admin", "staff"],
  },
  { text: "設定", icon: <SettingsIcon />, path: "/settings" },
];

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userProfile } = useAuth();
  const { darkMode, toggleDarkMode } = useCustomTheme();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(
    null
  );

  // 未読通知数取得
  // 未読通知数はクエリキャッシュのstaleTimeで制御（一定時間は再取得しない）
  const { data: unreadCount } = useUnreadNotificationCount({
    staleTime: 60 * 1000 * 5, // 5分間はキャッシュを新鮮とみなす
    refetchOnWindowFocus: false, // フォーカス時の自動再取得を抑制
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      handleUserMenuClose();
      navigate("/");
    } catch (error) {
      console.error("ログアウトエラー:", error);
    }
  };

  const isItemVisible = useMemo(
    () => (item: NavigationItem) => {
      if (item.public) return true;
      if (!currentUser) return false; // User is not logged in, and item is not public

      // From here, user is logged in
      if (!item.roles) return true; // No specific roles required
      if (!userProfile) return false; // Should not happen if currentUser exists, but for type safety

      return item.roles.some((role) => userProfile.roles.includes(role));
    },
    [currentUser, userProfile]
  );

  const drawer = (
    <Box>
      <Toolbar>
        <Typography
          variant="h6"
          noWrap
          component="div"
          sx={{ fontWeight: "bold" }}
        >
          🔮 Arcana
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {navigationItems
          .filter((item) => isItemVisible(item))
          .map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) {
                    setMobileOpen(false);
                  }
                }}
                sx={{
                  mx: 1,
                  my: 0.5,
                  borderRadius: 2,
                  "&.Mui-selected": {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark,
                    },
                    "& .MuiListItemIcon-root": {
                      color: theme.palette.primary.contrastText,
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color:
                      location.pathname === item.path
                        ? theme.palette.primary.contrastText
                        : theme.palette.text.primary,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { lg: `calc(100% - ${drawerWidth}px)` },
          ml: { lg: `${drawerWidth}px` },
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
        }}
        elevation={0}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { lg: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Lost & Found System
          </Typography>

          {currentUser ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={darkMode}
                    onChange={toggleDarkMode}
                    icon={<LightModeIcon />}
                    checkedIcon={<DarkModeIcon />}
                  />
                }
                label=""
              />
              <IconButton
                size="large"
                aria-label="notifications"
                color="inherit"
                onClick={() => navigate("/notifications")}
              >
                <Badge badgeContent={unreadCount || 0} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleUserMenuOpen}
                color="inherit"
              >
                <Avatar
                  alt={currentUser.displayName || currentUser.email || ""}
                  src={currentUser.photoURL || ""}
                  sx={{ width: 32, height: 32 }}
                >
                  {currentUser.email?.[0]?.toUpperCase()}
                </Avatar>
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={userMenuAnchor}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(userMenuAnchor)}
                onClose={handleUserMenuClose}
              >
                <MenuItem disabled>
                  <Typography variant="body2">{currentUser.email}</Typography>
                </MenuItem>
                {userProfile && (
                  <MenuItem disabled>
                    <Typography variant="caption" color="text.secondary">
                      Role: {userProfile.roles.join(", ")}
                    </Typography>
                  </MenuItem>
                )}
                <Divider />
                <MenuItem
                  onClick={() => {
                    navigate("/profile");
                    handleUserMenuClose();
                  }}
                >
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  プロフィール
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  ログアウト
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <Button
              color="primary"
              variant="contained"
              onClick={() => navigate("/login")}
              sx={{ ml: 2 }}
            >
              ログイン
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { lg: drawerWidth }, flexShrink: { lg: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", lg: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", lg: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { lg: `calc(100% - ${drawerWidth}px)` },
          mt: "64px",
          minHeight: "calc(100vh - 64px)",
          backgroundColor: theme.palette.background.default,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default AppLayout;
